import ReactGA from "react-ga4";
import initializeGa from "core/utils/initializeGa";
import { initializeYm, ymHit, ymReachGoal } from "core/utils/ym";
import { detectAdblock } from "core/utils/adblock";

/**
 * Единая точка отправки аналитики: Google Analytics и Яндекс.Метрика.
 *
 * Компоненты не знают, куда именно уезжает событие, они зовут
 * `analytics.event` и `analytics.send` с той же сигнатурой, что была у
 * `react-ga4`. Добавить третью систему теперь можно здесь, а не в полусотне
 * файлов.
 *
 * Контекст (`setAnalyticsContext`) прилипает ко всем последующим событиям
 * обеих систем. Туда кладётся `surface`, то есть где именно всё
 * происходит: `embed` для плеера в чужом iframe и `arena` для сайта арены.
 * По нему в отчётах отделяется воронка эмбеда от остального трафика, а
 * `embed_host` показывает, на каком домене этот эмбед стоит.
 */
const GA_RESERVED_KEYS = ["category", "action", "label", "value", "hitType"];

let context = {};

/**
 * Номер счётчика Метрики держим рядом с контекстом, но наружу как параметр
 * события не отдаём: это служебное значение, а не измерение.
 */
let ymCounterId = null;

/**
 * Имя цели Метрики для зрителей с блокировщиком рекламы.
 */
export const ADBLOCK_DETECTED = "adblock_detected";

/**
 * Проверка блокировщика запускается один раз за жизнь страницы. Флаг нужен
 * потому, что initialize зовут из тела компонента, то есть на каждый
 * рендер приложения.
 */
let adblockChecked = false;

/**
 * Результат проверки уезжает двумя путями. Параметром контекста, чтобы
 * любой шаг воронки можно было разложить на зрителей с блокировщиком и
 * без, и отдельной целью, но только когда блокировщик действительно есть:
 * цель Метрики умеет считать достижения, а не значения, и такой вид
 * сразу даёт долю визитов с блокировкой.
 */
function checkAdblock() {
  if (adblockChecked) return;

  adblockChecked = true;

  detectAdblock()
    .then((blocked) => {
      if (blocked !== undefined) {
        setAnalyticsContext({ adblock: blocked });

        if (blocked) {
          analytics.track(ADBLOCK_DETECTED);
        }
      }

      return blocked;
    })
    .catch(() => undefined);
}

/** Контекст без служебных полей: именно он уезжает в обе системы. */
function publicContext() {
  return context;
}

/**
 * Идентификатор цели Метрики из пары category и action: у Метрики нет
 * измерений GA, есть только имя цели, поэтому склеиваем их в
 * `player_unmute`, `ad_no_fill` и подобное. Латиница, нижний регистр,
 * разделитель подчёркивание: такие имена принимает интерфейс Метрики.
 */
export function goalName(category, action) {
  return [category, action]
    .filter(Boolean)
    .join("_")
    .replace(/([a-z\d])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z\d]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Дополнить контекст, который уезжает со всеми следующими событиями.
 */
export function setAnalyticsContext(values) {
  // На сервере контекст не нужен и вреден: модуль живёт один на процесс,
  // а его значение принадлежит конкретному посетителю.
  if (!process.browser) return;

  const next = { ...context, ...values };

  // Точки вызова живут в теле компонентов и повторяются на каждом
  // рендере. Без этой проверки Метрика получала бы запрос с параметрами
  // визита на каждую перерисовку приложения.
  if (JSON.stringify(next) === JSON.stringify(context)) return;

  context = next;

  // GA4 подмешивает это во все последующие хиты сам.
  if (ReactGA.isInitialized) {
    ReactGA.set({ ...publicContext() });
  }

  // В Метрике то же самое живёт как параметры визита.
  if (typeof window.ym === "function" && ymCounterId) {
    window.ym(ymCounterId, "params", { ...publicContext() });
  }
}

export function getAnalyticsContext() {
  return publicContext();
}

/**
 * Ключи параметров в GA4 и Метрике принято писать в нижнем регистре через
 * подчёркивание, а в коде переменные camelCase. Переводим на границе.
 */
function toSnakeKeys(params) {
  return Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc;

    acc[key.replace(/([a-z\d])([A-Z])/g, "$1_$2").toLowerCase()] = value;

    return acc;
  }, {});
}

const analytics = {
  /**
   * Поднять обе системы по настройкам арены: `gaId` и `ymId`.
   */
  initialize(arena) {
    initializeGa(arena);
    initializeYm(arena);

    if (arena?.ymId) {
      ymCounterId = arena.ymId;
    }

    checkAdblock();
  },

  /**
   * Событие с произвольными параметрами, в терминах GA4 и целей Метрики.
   *
   * Отдельный метод, потому что объектная форма `react-ga4` понимает
   * только category, action, label и value, а остальные поля молча
   * выбрасывает: воронка эмбеда так потеряла бы и surface, и тип
   * креатива. Строковая форма отправляет их как параметры события.
   */
  track(name, params) {
    if (!process.browser) return;
    if (!name) return;

    const payload = toSnakeKeys({ ...publicContext(), ...params });

    if (ReactGA.isInitialized) {
      ReactGA.event(name, payload);
    }

    ymReachGoal(name, payload);
  },

  /**
   * Событие. Сигнатура сохранена от `react-ga4`: category, action, label,
   * value, плюс произвольные поля, которые уедут параметрами.
   */
  event(params) {
    if (!process.browser) return;
    if (!params) return;

    const { category, action, label, value } = params;

    const extra = Object.entries(params).reduce((acc, [key, item]) => {
      if (GA_RESERVED_KEYS.includes(key)) return acc;

      acc[key] = item;

      return acc;
    }, {});

    if (ReactGA.isInitialized) {
      ReactGA.event({ ...params, ...publicContext() });
    }

    ymReachGoal(goalName(category, action), {
      ...publicContext(),
      ...extra,
      label,
      value,
    });
  },

  /**
   * Просмотр страницы. В SPA это единственный способ учесть переходы:
   * браузер страницу не перезагружает, и обе системы сами о них не узнают.
   */
  send(params) {
    if (!process.browser) return;
    if (!params) return;

    if (ReactGA.isInitialized) {
      ReactGA.send({ ...params, ...publicContext() });
    }

    if (params.hitType === "pageview") {
      ymHit(params.page, params.title);
    }
  },
};

export default analytics;
